// Phase E — Customer claims (portal).
//
// Two roles served by one file:
//   • `PortalClaims` page (route /portal/claims) — list of customer's claims.
//   • `FileClaimSection` component — embedded in PortalTimeline, only
//     visible when the deal is executed AND inside the 5-day SLA.
import React, { useState, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { ShieldAlert, FilePlus, Loader2, AlertCircle, CheckCircle2, Clock, Paperclip, X } from "lucide-react";
import { toast } from "sonner";
import { useT } from "../../lib/i18n";
import { useClaimTypes, useClaims, useCreateClaim, useUploadClaimEvidence } from "../../api/claims";

const mono = (size, color, weight, ls) => ({
  fontFamily: "'DM Mono', monospace",
  fontSize: size,
  color: color || "var(--text, #f0f2f8)",
  fontWeight: weight || 400,
  letterSpacing: ls,
});

const inputStyle = {
  background: "var(--bg, #0a0c14)",
  border: "1px solid rgba(255,255,255,0.12)",
  color: "var(--text, #f0f2f8)",
  fontFamily: "'DM Mono', monospace",
  fontSize: 12,
  padding: "9px 10px",
  borderRadius: 6,
  width: "100%",
};

const STATUS_COLOR = {
  open: "#79b5f0",
  under_review: "#f3b14a",
  resolved: "#4ed084",
  rejected: "#ff8a8a",
};
const STATUS_KEY = {
  open: "portal.claims.status_open",
  under_review: "portal.claims.status_under_review",
  resolved: "portal.claims.status_resolved",
  rejected: "portal.claims.status_rejected",
};

export const StatusPill = ({ status, t }) => {
  const c = STATUS_COLOR[status] || "#9aa1b3";
  return (
    <span
      className="inline-flex items-center gap-1 px-2 py-0.5 rounded"
      style={{ background: `${c}22`, color: c, border: `1px solid ${c}55`, ...mono(10, c, 700, "0.6px"), textTransform: "uppercase" }}
      data-testid={`claim-status-${status}`}
    >
      {t(STATUS_KEY[status] || "portal.claims.status_open")}
    </span>
  );
};

/* ─── Embedded "File a claim" form (used on PortalTimeline) ─────────── */

export const FileClaimSection = ({ logisticsRequestId, executedAt }) => {
  const { t, locale } = useT();
  const { data: typesData } = useClaimTypes();
  const create = useCreateClaim();
  const upload = useUploadClaimEvidence();
  const fileInputRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [type, setType] = useState("");
  const [description, setDescription] = useState("");
  const [files, setFiles] = useState([]);
  const [err, setErr] = useState(null);

  // 5-day window check on the FE — backend enforces the same rule.
  // We snapshot "now" into state once on mount; the backend is the source
  // of truth so a stale render is acceptable.
  const [nowMs] = useState(() => Date.now());
  const windowClosed = useMemo(() => {
    if (!executedAt) return false;
    const slaMs = 5 * 24 * 60 * 60 * 1000;
    return nowMs - Date.parse(executedAt) > slaMs;
  }, [executedAt, nowMs]);
  if (!executedAt) return null;  // only render once executed
  if (windowClosed) {
    return (
      <section
        className="rounded p-4 mt-6"
        style={{ background: "var(--bg-surface, #0d0f18)", border: "1px solid rgba(120,128,148,0.30)" }}
        data-testid="file-claim-window-closed"
      >
        <div style={mono(11, "var(--text-dim, #9aa1b3)")} className="flex items-center gap-2">
          <Clock size={13} /> {t("portal.claims.no_claim_window")}
        </div>
      </section>
    );
  }

  const onSubmit = async (e) => {
    e?.preventDefault?.();
    setErr(null);
    if (!type) { setErr(t("portal.claims.type")); return; }
    if (!description || description.length < 5) { setErr(t("portal.claims.description")); return; }
    try {
      const claim = await create.mutateAsync({
        logistics_request_id: logisticsRequestId,
        claim_type: type,
        description,
      });
      if (files.length > 0) {
        try { await upload.mutateAsync({ claimId: claim.id, files }); }
        catch (_) { /* evidence upload failure shouldn't lose the claim itself */ }
      }
      toast.success(t("portal.claims.submit"));
      setOpen(false); setType(""); setDescription(""); setFiles([]);
    } catch (e2) {
      const det = e2?.response?.data?.detail || {};
      if (det?.code === "out_of_window") setErr(t("portal.claims.out_of_window"));
      else setErr(det?.message || e2?.message || "Failed");
    }
  };

  if (!open) {
    return (
      <section
        className="rounded p-4 mt-6 flex items-center justify-between gap-3 flex-wrap"
        style={{ background: "rgba(226,75,74,0.05)", border: "1px solid rgba(226,75,74,0.30)" }}
        data-testid="file-claim-cta"
      >
        <div style={mono(11, "var(--text-dim, #cfd5e3)")}>
          <ShieldAlert size={13} className="inline mr-1" color="#ff8a8a" />
          {t("portal.claims.subtitle")}
        </div>
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="inline-flex items-center gap-2 px-3 py-2 rounded"
          style={{ background: "rgba(226,75,74,0.20)", color: "#ff8a8a", border: "1px solid rgba(226,75,74,0.50)", ...mono(11, "#ff8a8a", 700, "0.8px"), textTransform: "uppercase", cursor: "pointer" }}
          data-testid="file-claim-open"
        >
          <FilePlus size={12} /> {t("portal.claims.file_btn")}
        </button>
      </section>
    );
  }

  const types = typesData?.types || [];

  return (
    <form
      onSubmit={onSubmit}
      className="rounded p-4 mt-6 space-y-3"
      style={{ background: "var(--bg-surface, #0d0f18)", border: "1px solid rgba(226,75,74,0.40)" }}
      data-testid="file-claim-form"
    >
      <div className="flex items-center justify-between">
        <div style={{ ...mono(10, "#ff8a8a", 700, "1.2px"), textTransform: "uppercase" }}>
          <ShieldAlert size={13} className="inline mr-1" />
          {t("portal.claims.file_btn")}
        </div>
        <button type="button" onClick={() => setOpen(false)} style={{ background: "transparent", border: "none", color: "#9aa1b3", cursor: "pointer" }} data-testid="file-claim-cancel">
          <X size={14} />
        </button>
      </div>
      <label className="block">
        <div style={{ ...mono(9, "var(--text-dim, #9aa1b3)", 700, "1px"), textTransform: "uppercase", marginBottom: 4 }}>
          {t("portal.claims.type")} *
        </div>
        <select value={type} onChange={(e) => setType(e.target.value)} style={inputStyle} data-testid="file-claim-type">
          <option value="">—</option>
          {types.map((t2) => (
            <option key={t2.key} value={t2.key}>{t2.labels[locale] || t2.labels.en}</option>
          ))}
        </select>
      </label>
      <label className="block">
        <div style={{ ...mono(9, "var(--text-dim, #9aa1b3)", 700, "1px"), textTransform: "uppercase", marginBottom: 4 }}>
          {t("portal.claims.description")} *
        </div>
        <textarea
          rows={4} value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder={t("portal.claims.description_placeholder")}
          style={{ ...inputStyle, fontFamily: "'DM Mono', monospace" }}
          data-testid="file-claim-description"
        />
      </label>
      <div>
        <div style={{ ...mono(9, "var(--text-dim, #9aa1b3)", 700, "1px"), textTransform: "uppercase", marginBottom: 4 }}>
          {t("portal.claims.evidence")}
        </div>
        <input
          ref={fileInputRef}
          type="file"
          multiple
          onChange={(e) => setFiles(Array.from(e.target.files || []).slice(0, 10))}
          accept=".jpg,.jpeg,.png,.gif,.webp,.heic,.heif,.pdf,.doc,.docx,.xlsx,.csv,.txt"
          style={{ ...mono(10, "var(--text-dim, #cfd5e3)") }}
          data-testid="file-claim-evidence-input"
        />
        <div className="mt-1" style={mono(10, "var(--text-dim, #6b7388)")}>
          {t("portal.claims.evidence_hint")}
        </div>
        {files.length > 0 && (
          <ul className="mt-2 space-y-1" data-testid="file-claim-evidence-list">
            {files.map((f, i) => (
              <li key={i} style={mono(10, "var(--text-dim, #cfd5e3)")} className="flex items-center gap-2">
                <Paperclip size={11} /> {f.name} <span style={mono(9, "var(--text-dim, #6b7388)")}>· {(f.size / 1024).toFixed(0)} KB</span>
              </li>
            ))}
          </ul>
        )}
      </div>
      {err && (
        <div className="rounded px-3 py-2 inline-flex items-start gap-2"
             style={{ background: "rgba(226,75,74,0.10)", border: "1px solid rgba(226,75,74,0.40)", color: "#ff8a8a", ...mono(11), width: "100%" }}
             data-testid="file-claim-error">
          <AlertCircle size={13} className="mt-0.5" /> {err}
        </div>
      )}
      <div className="flex justify-end">
        <button
          type="submit"
          disabled={create.isPending || upload.isPending}
          className="px-3 py-2 rounded inline-flex items-center gap-2"
          style={{
            background: "#E24B4A", color: "#fff", border: "none",
            ...mono(11, "#fff", 700, "0.8px"), textTransform: "uppercase",
            opacity: (create.isPending || upload.isPending) ? 0.6 : 1, cursor: "pointer",
          }}
          data-testid="file-claim-submit"
        >
          {create.isPending ? <Loader2 size={12} className="animate-spin" /> : <FilePlus size={12} />}
          {create.isPending ? t("portal.claims.submitting") : t("portal.claims.submit")}
        </button>
      </div>
    </form>
  );
};

/* ─── Page: /portal/claims ─────────────────────────────────────────── */

export default function PortalClaims() {
  const { t } = useT();
  const navigate = useNavigate();
  const { data, isLoading } = useClaims({});
  const items = data?.items || [];

  return (
    <div data-testid="portal-claims-page">
      <header className="mb-5">
        <h1 style={{ fontFamily: "Syne, sans-serif", fontWeight: 800, fontSize: "clamp(22px, 3vw, 28px)", color: "var(--text, #f0f2f8)" }}>
          <ShieldAlert size={22} style={{ display: "inline", marginRight: 8, marginBottom: 4 }} />
          {t("portal.claims.title")}
        </h1>
        <div className="mt-1" style={mono(11, "var(--text-dim, #c5cad8)")}>
          {t("portal.claims.subtitle")}
        </div>
      </header>
      <div
        className="rounded"
        style={{ background: "var(--bg-surface, #0d0f18)", border: "1px solid rgba(255,255,255,0.10)" }}
      >
        {isLoading && <div className="p-6 text-center" style={mono(11, "var(--text-dim, #9aa1b3)")}>{t("portal.list.loading")}</div>}
        {!isLoading && items.length === 0 && (
          <div className="p-8 text-center" style={mono(12, "var(--text-dim, #cfd5e3)")} data-testid="portal-claims-empty">
            {t("portal.claims.empty")}
          </div>
        )}
        {items.length > 0 && (
          <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 700 }}>
            <thead>
              <tr style={{ background: "rgba(255,255,255,0.03)" }}>
                {[t("portal.claims.type"), t("portal.claims.description"), t("portal.claims.status_label"), t("portal.claims.filed"), ""].map((h, i) => (
                  <th key={i} style={{ padding: "8px 12px", textAlign: "left", ...mono(9, "var(--text-dim, #9aa1b3)", 700, "1.2px"), textTransform: "uppercase", borderBottom: "1px solid rgba(255,255,255,0.08)" }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {items.map((c) => (
                <tr
                  key={c.id} data-testid={`portal-claim-row-${c.id}`}
                  style={{ borderBottom: "1px solid rgba(255,255,255,0.05)", cursor: "pointer" }}
                  onClick={() => navigate(`/portal/r/${c.logistics_request_id}`)}
                  onMouseEnter={(e) => (e.currentTarget.style.background = "rgba(155,89,182,0.05)")}
                  onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
                >
                  <td style={{ padding: "10px 12px", ...mono(11) }}>{c.claim_type_label}</td>
                  <td style={{ padding: "10px 12px", ...mono(11, "var(--text-dim, #cfd5e3)"), maxWidth: 380, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{c.description}</td>
                  <td style={{ padding: "10px 12px" }}><StatusPill status={c.status} t={t} /></td>
                  <td style={{ padding: "10px 12px", ...mono(10, "var(--text-dim, #9aa1b3)") }}>{(c.created_at || "").slice(0, 16).replace("T", " ")}</td>
                  <td style={{ padding: "10px 12px", textAlign: "right" }}>
                    <CheckCircle2 size={14} color="#6b7388" />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
